'use strict';

/**
 * Prompt shape manager, one view serving both ends of the cascade:
 *
 *   Organization scope (the /prompt-shapes settings page, users.manage): the
 *   platform catalog with adopt buttons and drift badges, plus the org's own
 *   shapes - create fresh, edit segments, retire, history.
 *
 *   User scope (the My Shapes tab inside the Prompt Library): the org's active
 *   shapes with fork buttons, plus the caller's personal shapes with the same
 *   editor.
 *
 * The segment editor is the same in both scopes; the server enforces who may
 * write what (org shapes need users.manage, personal shapes their owner).
 *
 * @author Steven Velozo <steven@velozo.com>
 * @license MIT
 */

const libPictView = require('pict-view');

function _escapeHTML(pStr)
{
	return String(pStr == null ? '' : pStr)
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function _slug(pText)
{
	return String(pText || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'segment';
}

const _ViewConfiguration =
{
	ViewIdentifier: 'Plansheet-PromptShapes',
	DefaultRenderable: 'Plansheet-PromptShapes',
	DefaultDestinationAddress: '#Plansheet-Workspace',
	AutoRender: false,

	CSS: /*css*/`
		.pps { display: flex; flex-direction: column; gap: 18px; padding: 14px 18px; box-sizing: border-box; max-width: 1100px; }
		.pps h2 { margin: 0; font-size: 19px; }
		.pps h3 { margin: 0 0 8px 0; font-size: 15px; color: var(--theme-color-text-secondary, #667); font-weight: 600; }
		.pps-intro { color: var(--theme-color-text-secondary, #667); font-size: 13px; margin: 2px 0 0 0; }
		.pps-section { border: 1px solid var(--theme-color-border-light, #e3e3e3); border-radius: 8px; padding: 12px 14px; background: var(--theme-color-background-secondary, transparent); }
		.pps-row { display: flex; align-items: center; gap: 10px; padding: 7px 4px; border-bottom: 1px solid var(--theme-color-border-light, #eee); }
		.pps-row:last-child { border-bottom: none; }
		.pps-row-name { font-weight: 600; min-width: 170px; }
		.pps-row-desc { flex: 1; color: var(--theme-color-text-secondary, #667); font-size: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.pps-badge { font-size: 11px; padding: 2px 8px; border-radius: 9px; border: 1px solid var(--theme-color-border-light, #ccc); color: var(--theme-color-text-secondary, #667); white-space: nowrap; }
		.pps-badge-adopted { border-color: var(--theme-color-brand-primary, #2e7d74); color: var(--theme-color-brand-primary, #2e7d74); }
		.pps-badge-drift { border-color: var(--theme-color-status-warning, #c77c02); color: var(--theme-color-status-warning, #c77c02); }
		.pps-badge-retired { opacity: 0.7; }
		.pps-btn { border: 1px solid var(--theme-color-border-light, #ccc); background: var(--theme-color-background-primary, #fff); color: var(--theme-color-text-primary, #223); border-radius: 6px; padding: 4px 11px; font-size: 12.5px; cursor: pointer; white-space: nowrap; }
		.pps-btn:hover { border-color: var(--theme-color-brand-primary, #2e7d74); color: var(--theme-color-brand-primary, #2e7d74); }
		.pps-btn[disabled] { opacity: 0.45; cursor: default; }
		.pps-btn-primary { background: var(--theme-color-brand-primary, #2e7d74); border-color: var(--theme-color-brand-primary, #2e7d74); color: var(--theme-color-text-on-brand, #fff); }
		.pps-btn-primary:hover { color: var(--theme-color-text-on-brand, #fff); opacity: 0.92; }
		.pps-empty { color: var(--theme-color-text-secondary, #889); font-size: 13px; padding: 8px 4px; }
		.pps-editor { display: flex; flex-direction: column; gap: 10px; }
		.pps-editor label { font-size: 12px; color: var(--theme-color-text-secondary, #667); display: block; margin-bottom: 3px; }
		.pps-input, .pps-textarea { width: 100%; box-sizing: border-box; border: 1px solid var(--theme-color-border-light, #ccc); border-radius: 6px; padding: 6px 9px; font-size: 13.5px; background: var(--theme-color-background-primary, #fff); color: var(--theme-color-text-primary, #223); }
		.pps-textarea { font-family: inherit; min-height: 54px; resize: vertical; }
		.pps-seg { border: 1px solid var(--theme-color-border-light, #e3e3e3); border-radius: 7px; padding: 10px; display: flex; flex-direction: column; gap: 8px; }
		.pps-seg-grid { display: grid; grid-template-columns: 1fr 1.6fr auto; gap: 8px; align-items: end; }
		.pps-ref-grid { display: grid; grid-template-columns: 1fr 1.3fr 1.3fr auto; gap: 8px; align-items: end; }
		.pps-refs > label { font-weight: 600; color: var(--theme-color-text-primary, #223); }
		.pps-refs-hint { font-size: 11.5px; color: var(--theme-color-text-secondary, #889); margin: 0 0 6px; }
		.pps-seg-flags { display: flex; gap: 14px; align-items: center; font-size: 12.5px; color: var(--theme-color-text-secondary, #667); }
		.pps-seg-flags label { display: inline-flex; align-items: center; gap: 5px; margin: 0; }
		.pps-editor-actions { display: flex; gap: 8px; justify-content: flex-end; }
	`,

	Templates:
	[
		{
			Hash: 'Plansheet-PromptShapes',
			Template: /*html*/`
<div class="pps">
	<div>
		<h2>{~D:AppData.PromptShapes.Heading~}</h2>
		<p class="pps-intro">{~D:AppData.PromptShapes.Intro~}</p>
	</div>
	{~TS:Plansheet-PromptShapes-Platform-Section:AppData.PromptShapes.PlatformSlot~}
	<div class="pps-section">
		<h3>{~D:AppData.PromptShapes.OwnedHeading~}</h3>
		<div id="PromptShapes-Owned"></div>
		<div style="margin-top:10px;"><button class="pps-btn pps-btn-primary" onclick="_Pict.views['Plansheet-PromptShapes'].startNewShape()">New shape</button></div>
	</div>
	{~TS:Plansheet-PromptShapes-Source-Section:AppData.PromptShapes.SourceSlot~}
	{~TS:Plansheet-PromptShapes-Editor-Section:AppData.PromptShapes.EditorSlot~}
</div>`
		},
		{
			Hash: 'Plansheet-PromptShapes-Platform-Section',
			Template: /*html*/`
<div class="pps-section">
	<h3>Platform catalog</h3>
	<div id="PromptShapes-Platform"></div>
</div>`
		},
		{
			Hash: 'Plansheet-PromptShapes-Source-Section',
			Template: /*html*/`
<div class="pps-section">
	<h3>Organization shapes you can fork</h3>
	<div id="PromptShapes-Forkable"></div>
</div>`
		},
		{
			Hash: 'Plansheet-PromptShapes-Editor-Section',
			Template: /*html*/`
<div class="pps-section">
	<h3>{~D:Record.Title~}</h3>
	<div class="pps-editor" id="PromptShapes-Editor"></div>
</div>`
		}
	],

	Renderables:
	[
		{ RenderableHash: 'Plansheet-PromptShapes', TemplateHash: 'Plansheet-PromptShapes', ContentDestinationAddress: '#Plansheet-Workspace', RenderMethod: 'replace' }
	]
};

class PictViewPromptShapes extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		if (!this.pict.AppData.PromptShapes)
		{
			this.pict.AppData.PromptShapes =
			{
				Scope: 'Organization',
				Heading: 'Prompt shapes',
				Intro: '',
				OwnedHeading: '',
				Platform: [],
				Tenant: [],
				PlatformSlot: [],
				SourceSlot: [],
				EditorSlot: [],
				Editing: null
			};
		}
		this._destination = null;
	}

	_state() { return this.pict.AppData.PromptShapes; }
	_api() { return this.pict.providers.PromptShapeAPI; }
	_modal() { return this.pict.views['Pict-Section-Modal']; }
	_me() { return (this.pict.AppData.Auth && this.pict.AppData.Auth.User) || {}; }

	/**
	 * Enter the manager. pScope 'Organization' (settings page) or 'User'
	 * (the My Shapes tab); pDestinationAddress overrides where it renders.
	 */
	showIn(pDestinationAddress, pScope, pRouted)
	{
		let tmpState = this._state();
		tmpState.Scope = (pScope === 'User') ? 'User' : 'Organization';
		tmpState.Editing = null;
		// Only the standalone /prompt-shapes page is routed (pRouted): its edits navigate to /prompt-shape/:id
		// so they deep-link + survive reload. The embedded mounts (Prompt Library tab, org settings) are not
		// routed, so their editor stays inline.
		this._routed = !!pRouted;
		this._destination = pDestinationAddress || this.options.DefaultDestinationAddress;
		if (tmpState.Scope === 'Organization')
		{
			tmpState.Heading = 'Organization prompt shapes';
			tmpState.Intro = 'The prompt shapes everyone in the organization crafts against. Adopt a platform default to make it yours to edit, or author one fresh. Retired shapes stop being offered; existing prompts keep rendering.';
			tmpState.OwnedHeading = 'Organization shapes';
		}
		else
		{
			tmpState.Heading = 'My prompt shapes';
			tmpState.Intro = 'Personal shapes only you see in the editor. Fork an organization shape to adjust it for how you work, or author one fresh.';
			tmpState.OwnedHeading = 'My shapes';
		}
		return this._reload();
	}

	// Route entry for /prompt-shape/:id (edit) and /prompt-shapes/new (pID 0): render the standalone, routed
	// Organization page, then open the editor for that shape once the list has loaded.
	showRoutedEdit(pID)
	{
		let tmpEditID = Number(pID) || 0;
		let tmpSelf = this;
		return this.showIn('#Plansheet-Workspace', 'Organization', true).then(() =>
		{
			if (tmpEditID > 0) { tmpSelf._applyEdit(tmpEditID); } else { tmpSelf._applyNewShape(); }
		});
	}

	_reload()
	{
		let tmpState = this._state();
		return Promise.all([this._api().platform(), this._api().list()])
			.then((pResults) =>
			{
				tmpState.Platform = pResults[0] || [];
				tmpState.Tenant = pResults[1] || [];
				this._renderShell();
			})
			.catch(() => this._renderShell());
	}

	_renderShell()
	{
		let tmpState = this._state();
		tmpState.PlatformSlot = (tmpState.Scope === 'Organization') ? [{}] : [];
		tmpState.SourceSlot = (tmpState.Scope === 'User') ? [{}] : [];
		tmpState.EditorSlot = tmpState.Editing ? [{ Title: tmpState.Editing.IDPromptShape ? ('Edit: ' + tmpState.Editing.Name) : 'New shape' }] : [];
		this.render(this.options.DefaultRenderable, this._destination || this.options.DefaultDestinationAddress);
	}

	_ownedRows()
	{
		let tmpState = this._state();
		let tmpMe = Number(this._me().IDUser) || 0;
		if (tmpState.Scope === 'Organization')
		{
			return tmpState.Tenant.filter((pRow) => pRow.Scope === 'Organization');
		}
		return tmpState.Tenant.filter((pRow) => pRow.Scope === 'User' && Number(pRow.IDUser) === tmpMe);
	}

	// ---- list paints (innerHTML into the shell's containers) -----------------

	_paintLists()
	{
		let tmpState = this._state();

		if (tmpState.Scope === 'Organization')
		{
			let tmpHost = document.getElementById('PromptShapes-Platform');
			if (tmpHost)
			{
				let tmpOrgBySource = {};
				tmpState.Tenant.filter((pRow) => pRow.Scope === 'Organization' && pRow.IDSourcePlatformPromptShape)
					.forEach((pRow) => { tmpOrgBySource[pRow.IDSourcePlatformPromptShape] = pRow; });
				let tmpHTML = '';
				tmpState.Platform.forEach((pShape) =>
				{
					let tmpAdopted = tmpOrgBySource[pShape.IDPlatformPromptShape];
					let tmpDrift = tmpAdopted && (Number(pShape.Version) > Number(tmpAdopted.SourceVersion));
					tmpHTML += '<div class="pps-row">'
						+ '<span class="pps-row-name">' + _escapeHTML(pShape.Name) + '</span>'
						+ '<span class="pps-row-desc">' + _escapeHTML(pShape.Description || '') + '</span>'
						+ (tmpAdopted ? '<span class="pps-badge pps-badge-adopted">Adopted</span>' : '')
						+ (tmpDrift ? '<span class="pps-badge pps-badge-drift">Platform updated</span>' : '')
						+ '<button class="pps-btn" ' + (tmpAdopted ? 'disabled' : '')
						+ ' onclick="_Pict.views[\'Plansheet-PromptShapes\'].adopt(' + pShape.IDPlatformPromptShape + ')">Adopt</button>'
						+ '</div>';
				});
				tmpHost.innerHTML = tmpHTML || '<div class="pps-empty">No platform shapes are published.</div>';
			}
		}

		if (tmpState.Scope === 'User')
		{
			let tmpHost = document.getElementById('PromptShapes-Forkable');
			if (tmpHost)
			{
				let tmpOrgShapes = tmpState.Tenant.filter((pRow) => pRow.Scope === 'Organization' && pRow.Status === 'Active');
				let tmpHTML = '';
				tmpOrgShapes.forEach((pRow) =>
				{
					tmpHTML += '<div class="pps-row">'
						+ '<span class="pps-row-name">' + _escapeHTML(pRow.Name) + '</span>'
						+ '<span class="pps-row-desc">' + _escapeHTML(pRow.Description || '') + '</span>'
						+ '<button class="pps-btn" onclick="_Pict.views[\'Plansheet-PromptShapes\'].fork(' + pRow.IDPromptShape + ')">Fork to mine</button>'
						+ '</div>';
				});
				tmpHost.innerHTML = tmpHTML || '<div class="pps-empty">The organization has no active shapes yet. Platform defaults are always available in the editor; ask an admin to adopt one if you want a forkable org copy.</div>';
			}
		}

		let tmpOwnedHost = document.getElementById('PromptShapes-Owned');
		if (tmpOwnedHost)
		{
			let tmpRows = this._ownedRows();
			let tmpHTML = '';
			tmpRows.forEach((pRow) =>
			{
				let tmpRetired = pRow.Status === 'Retired';
				tmpHTML += '<div class="pps-row">'
					+ '<span class="pps-row-name">' + _escapeHTML(pRow.Name) + '</span>'
					+ '<span class="pps-row-desc">' + _escapeHTML(pRow.Description || '') + '</span>'
					+ '<span class="pps-badge' + (tmpRetired ? ' pps-badge-retired' : '') + '">' + _escapeHTML(pRow.SourceType || 'Fresh') + (tmpRetired ? ' / Retired' : '') + '</span>'
					+ '<button class="pps-btn" onclick="_Pict.views[\'Plansheet-PromptShapes\'].edit(' + pRow.IDPromptShape + ')">Edit</button>'
					+ '<button class="pps-btn" onclick="_Pict.views[\'Plansheet-PromptShapes\'].history(' + pRow.IDPromptShape + ')">History</button>'
					+ '<button class="pps-btn" onclick="_Pict.views[\'Plansheet-PromptShapes\'].toggleRetired(' + pRow.IDPromptShape + ')">' + (tmpRetired ? 'Reactivate' : 'Retire') + '</button>'
					+ '<button class="pps-btn" onclick="_Pict.views[\'Plansheet-PromptShapes\'].removeShape(' + pRow.IDPromptShape + ')">Delete</button>'
					+ '</div>';
			});
			tmpOwnedHost.innerHTML = tmpHTML || '<div class="pps-empty">Nothing here yet.</div>';
		}

		this._paintEditor();
	}

	// ---- actions ---------------------------------------------------------------

	adopt(pIDPlatformPromptShape)
	{
		this._api().adoptPlatform({ IDPlatformPromptShape: pIDPlatformPromptShape })
			.then(() => { this._toast('Adopted. The organization copy is yours to edit.', 'success'); return this._reload(); })
			.catch((pError) => this._toast(pError.message, 'error'));
	}

	fork(pIDPromptShape)
	{
		this._api().fork(pIDPromptShape)
			.then(() => { this._toast('Forked into your shapes.', 'success'); return this._reload(); })
			.catch((pError) => this._toast(pError.message, 'error'));
	}

	toggleRetired(pIDPromptShape)
	{
		let tmpRow = this._state().Tenant.find((pCandidate) => Number(pCandidate.IDPromptShape) === Number(pIDPromptShape));
		if (!tmpRow) { return; }
		this._api().update(pIDPromptShape, { Status: (tmpRow.Status === 'Retired') ? 'Active' : 'Retired' })
			.then(() => this._reload())
			.catch((pError) => this._toast(pError.message, 'error'));
	}

	removeShape(pIDPromptShape)
	{
		let tmpRow = this._state().Tenant.find((pCandidate) => Number(pCandidate.IDPromptShape) === Number(pIDPromptShape));
		if (!tmpRow) { return; }
		this._modal().confirm('Prompts already crafted against it keep rendering; the shape stops being offered. The delete is soft and its history stays.',
			{ title: 'Delete shape "' + tmpRow.Name + '"?', confirmLabel: 'Delete', dangerous: true })
			.then((pOk) =>
			{
				if (!pOk) { return; }
				this._api().remove(pIDPromptShape)
					.then(() => this._reload())
					.catch((pError) => this._toast(pError.message, 'error'));
			});
	}

	history(pIDPromptShape)
	{
		this._api().versions(pIDPromptShape).then((pBody) =>
		{
			let tmpVersions = pBody.Versions || [];
			let tmpHTML = tmpVersions.length ? '' : '<p>No versions captured yet.</p>';
			tmpVersions.forEach((pVersion) =>
			{
				let tmpDefinition = {};
				try { tmpDefinition = JSON.parse(pVersion.ShapeDefinition || '{}'); } catch (pError) { tmpDefinition = {}; }
				let tmpSegments = (tmpDefinition.Segments || []).map((pSegment) => pSegment.Name).join(', ');
				tmpHTML += '<div style="border-bottom:1px solid #ddd; padding:7px 0;">'
					+ '<strong>v' + pVersion.Sequence + '</strong>'
					+ (pVersion.Pinned ? ' <span style="font-size:11px;">(pinned)</span>' : '')
					+ ' <span style="font-size:12px; opacity:0.75;">' + _escapeHTML(pVersion.Note || '') + ' - ' + _escapeHTML(String(pVersion.UpdateDate || pVersion.CreateDate || '')) + '</span>'
					+ '<div style="font-size:12.5px; margin-top:3px;">' + _escapeHTML(tmpDefinition.Name || '') + ': ' + _escapeHTML(tmpSegments) + '</div>'
					+ '</div>';
			});
			this._modal().show(
			{
				title: 'Shape history: ' + ((pBody.Shape && pBody.Shape.Name) || ''),
				content: '<div style="max-height:50vh; overflow:auto;">' + tmpHTML + '</div>',
				buttons: [{ Hash: 'ok', Label: 'Close', Style: 'primary' }]
			});
		}).catch((pError) => this._toast(pError.message, 'error'));
	}

	// ---- the segment editor ------------------------------------------------------

	// Starting / editing a shape is a NAVIGATION on the standalone routed page (so an in-progress draft
	// deep-links + survives reload); embedded mounts (Prompt Library tab, org settings) edit inline.
	startNewShape()
	{
		let tmpApp = this.pict.PictApplication;
		if (this._routed && tmpApp && typeof tmpApp.showNewPromptShape === 'function') { tmpApp.showNewPromptShape(); return; }
		this._applyNewShape();
	}

	_applyNewShape()
	{
		let tmpState = this._state();
		tmpState.Editing =
		{
			IDPromptShape: 0,
			ShapeKey: 'shape-' + Math.random().toString(36).slice(2, 10),
			Name: '',
			Description: '',
			References: [],
			Segments: [{ Key: 'context', Name: 'Context', Guidance: '', Optional: false, Fixed: false, Body: '' }]
		};
		this._renderShell();
	}

	edit(pIDPromptShape)
	{
		let tmpApp = this.pict.PictApplication;
		if (this._routed && tmpApp && typeof tmpApp.showPromptShape === 'function') { tmpApp.showPromptShape(pIDPromptShape); return; }
		this._applyEdit(pIDPromptShape);
	}

	_applyEdit(pIDPromptShape)
	{
		let tmpRow = this._state().Tenant.find((pCandidate) => Number(pCandidate.IDPromptShape) === Number(pIDPromptShape));
		if (!tmpRow) { return; }
		let tmpDefinition = {};
		try { tmpDefinition = JSON.parse(tmpRow.ShapeDefinition || '{}'); } catch (pError) { tmpDefinition = {}; }
		this._state().Editing =
		{
			IDPromptShape: tmpRow.IDPromptShape,
			ShapeKey: tmpRow.ShapeKey,
			Name: tmpRow.Name || '',
			Description: tmpRow.Description || '',
			References: (tmpDefinition.References || []).map((pReference) => (
			{
				Label: pReference.Label || '',
				Path: pReference.Path || '',
				DocPath: pReference.DocPath || '',
				Note: pReference.Note || ''
			})),
			Segments: (tmpDefinition.Segments || []).map((pSegment) => (
			{
				Key: pSegment.Key || _slug(pSegment.Name),
				Name: pSegment.Name || '',
				Guidance: pSegment.Guidance || '',
				Optional: !!pSegment.Optional,
				Fixed: !!pSegment.Fixed,
				Body: pSegment.Body || ''
			}))
		};
		this._renderShell();
	}

	cancelEdit()
	{
		this._state().Editing = null;
		// On the standalone routed page, leaving the editor returns to /prompt-shapes; embedded stays inline.
		let tmpApp = this.pict.PictApplication;
		if (this._routed && tmpApp && typeof tmpApp.showPromptShapes === 'function') { tmpApp.showPromptShapes(); return; }
		this._renderShell();
	}

	_paintEditor()
	{
		let tmpState = this._state();
		let tmpHost = document.getElementById('PromptShapes-Editor');
		if (!tmpHost || !tmpState.Editing) { return; }
		let tmpEditing = tmpState.Editing;
		let tmpViewRef = "_Pict.views['Plansheet-PromptShapes']";

		let tmpHTML = '<div><label>Name</label>'
			+ '<input class="pps-input" id="PPS-Edit-Name" value="' + _escapeHTML(tmpEditing.Name) + '" oninput="' + tmpViewRef + '.editField(\'Name\', this.value)"></div>'
			+ '<div><label>Description</label>'
			+ '<textarea class="pps-textarea" id="PPS-Edit-Description" oninput="' + tmpViewRef + '.editField(\'Description\', this.value)">' + _escapeHTML(tmpEditing.Description) + '</textarea></div>';

		tmpHTML += '<div class="pps-refs"><label>Reference Material</label>'
			+ '<p class="pps-refs-hint">Guideline docs every generated prompt tells the session to read first. Give a local checkout path, a Docs Lake path, or both (the prompt renders both, and an agent with no checkout can pull the Docs Lake copy with the read_document MCP tool).</p>';
		(tmpEditing.References || []).forEach((pReference, pIndex) =>
		{
			tmpHTML += '<div class="pps-seg">'
				+ '<div class="pps-ref-grid">'
				+ '<div><label>Label</label><input class="pps-input" value="' + _escapeHTML(pReference.Label) + '" oninput="' + tmpViewRef + '.editReference(' + pIndex + ', \'Label\', this.value)"></div>'
				+ '<div><label>Local path</label><input class="pps-input" placeholder="~/Code/..." value="' + _escapeHTML(pReference.Path) + '" oninput="' + tmpViewRef + '.editReference(' + pIndex + ', \'Path\', this.value)"></div>'
				+ '<div><label>Docs Lake path</label><input class="pps-input" placeholder="reference/pict/conventions.md" value="' + _escapeHTML(pReference.DocPath) + '" oninput="' + tmpViewRef + '.editReference(' + pIndex + ', \'DocPath\', this.value)"></div>'
				+ '<button class="pps-btn" onclick="' + tmpViewRef + '.removeReference(' + pIndex + ')">Remove</button>'
				+ '</div>'
				+ '<div><label>Note (optional)</label><input class="pps-input" value="' + _escapeHTML(pReference.Note) + '" oninput="' + tmpViewRef + '.editReference(' + pIndex + ', \'Note\', this.value)"></div>'
				+ '</div>';
		});
		tmpHTML += '<div><button class="pps-btn" onclick="' + tmpViewRef + '.addReference()">Add reference</button></div></div>';

		tmpEditing.Segments.forEach((pSegment, pIndex) =>
		{
			tmpHTML += '<div class="pps-seg">'
				+ '<div class="pps-seg-grid">'
				+ '<div><label>Segment name</label><input class="pps-input" value="' + _escapeHTML(pSegment.Name) + '" oninput="' + tmpViewRef + '.editSegment(' + pIndex + ', \'Name\', this.value)"></div>'
				+ '<div><label>Guidance (one line for the author)</label><input class="pps-input" value="' + _escapeHTML(pSegment.Guidance) + '" oninput="' + tmpViewRef + '.editSegment(' + pIndex + ', \'Guidance\', this.value)"></div>'
				+ '<button class="pps-btn" onclick="' + tmpViewRef + '.removeSegment(' + pIndex + ')">Remove</button>'
				+ '</div>'
				+ '<div class="pps-seg-flags">'
				+ '<label><input type="checkbox" ' + (pSegment.Optional ? 'checked' : '') + ' onchange="' + tmpViewRef + '.editSegment(' + pIndex + ', \'Optional\', this.checked)"> Optional</label>'
				+ '<label><input type="checkbox" ' + (pSegment.Fixed ? 'checked' : '') + ' onchange="' + tmpViewRef + '.editSegment(' + pIndex + ', \'Fixed\', this.checked)"> Fixed preamble (locked text)</label>'
				+ '</div>'
				+ (pSegment.Fixed
					? '<div><label>Fixed body</label><textarea class="pps-textarea" oninput="' + tmpViewRef + '.editSegment(' + pIndex + ', \'Body\', this.value)">' + _escapeHTML(pSegment.Body) + '</textarea></div>'
					: '')
				+ '</div>';
		});

		tmpHTML += '<div><button class="pps-btn" onclick="' + tmpViewRef + '.addSegment()">Add segment</button></div>'
			+ '<div class="pps-editor-actions">'
			+ '<button class="pps-btn" onclick="' + tmpViewRef + '.cancelEdit()">Cancel</button>'
			+ '<button class="pps-btn pps-btn-primary" onclick="' + tmpViewRef + '.saveShape()">Save shape</button>'
			+ '</div>';

		tmpHost.innerHTML = tmpHTML;
	}

	editField(pField, pValue)
	{
		let tmpEditing = this._state().Editing;
		if (tmpEditing) { tmpEditing[pField] = pValue; }
	}

	editSegment(pIndex, pField, pValue)
	{
		let tmpEditing = this._state().Editing;
		if (!tmpEditing || !tmpEditing.Segments[pIndex]) { return; }
		tmpEditing.Segments[pIndex][pField] = pValue;
		// Toggling Fixed shows/hides the body textarea.
		if (pField === 'Fixed') { this._paintEditor(); }
	}

	addSegment()
	{
		let tmpEditing = this._state().Editing;
		if (!tmpEditing) { return; }
		tmpEditing.Segments.push({ Key: '', Name: '', Guidance: '', Optional: false, Fixed: false, Body: '' });
		this._paintEditor();
	}

	editReference(pIndex, pField, pValue)
	{
		let tmpEditing = this._state().Editing;
		if (!tmpEditing || !tmpEditing.References || !tmpEditing.References[pIndex]) { return; }
		tmpEditing.References[pIndex][pField] = pValue;
	}

	addReference()
	{
		let tmpEditing = this._state().Editing;
		if (!tmpEditing) { return; }
		if (!Array.isArray(tmpEditing.References)) { tmpEditing.References = []; }
		tmpEditing.References.push({ Label: '', Path: '', DocPath: '', Note: '' });
		this._paintEditor();
	}

	removeReference(pIndex)
	{
		let tmpEditing = this._state().Editing;
		if (!tmpEditing || !tmpEditing.References) { return; }
		tmpEditing.References.splice(pIndex, 1);
		this._paintEditor();
	}

	removeSegment(pIndex)
	{
		let tmpEditing = this._state().Editing;
		if (!tmpEditing) { return; }
		tmpEditing.Segments.splice(pIndex, 1);
		this._paintEditor();
	}

	saveShape()
	{
		let tmpState = this._state();
		let tmpEditing = tmpState.Editing;
		if (!tmpEditing) { return; }
		let tmpName = String(tmpEditing.Name || '').trim();
		if (!tmpName) { return this._toast('The shape needs a name.', 'error'); }
		let tmpSegments = tmpEditing.Segments
			.filter((pSegment) => String(pSegment.Name || '').trim())
			.map((pSegment) => (
			{
				Key: pSegment.Key || _slug(pSegment.Name),
				Name: String(pSegment.Name).trim(),
				Guidance: String(pSegment.Guidance || ''),
				Optional: !!pSegment.Optional || undefined,
				Fixed: !!pSegment.Fixed || undefined,
				Body: pSegment.Fixed ? String(pSegment.Body || '') : undefined
			}));
		if (!tmpSegments.length) { return this._toast('A shape needs at least one segment.', 'error'); }

		// Keep only references that name something usable (a label plus at least one
		// place to read it). An all-empty row the author added and left blank drops out.
		let tmpReferences = (tmpEditing.References || [])
			.map((pReference) => (
			{
				Label: String(pReference.Label || '').trim(),
				Path: String(pReference.Path || '').trim(),
				DocPath: String(pReference.DocPath || '').trim(),
				Note: String(pReference.Note || '').trim()
			}))
			.filter((pReference) => pReference.Label && (pReference.Path || pReference.DocPath || pReference.Note))
			.map((pReference) => (
			{
				Label: pReference.Label,
				Path: pReference.Path || undefined,
				DocPath: pReference.DocPath || undefined,
				Note: pReference.Note || undefined
			}));

		let tmpDefinition =
		{
			Key: tmpEditing.ShapeKey,
			Name: tmpName,
			Description: String(tmpEditing.Description || ''),
			Segments: tmpSegments
		};
		if (tmpReferences.length) { tmpDefinition.References = tmpReferences; }
		let tmpRecord =
		{
			Name: tmpName.slice(0, 200),
			Description: String(tmpEditing.Description || ''),
			ShapeDefinition: JSON.stringify(tmpDefinition)
		};

		let tmpSave;
		if (tmpEditing.IDPromptShape)
		{
			tmpSave = this._api().update(tmpEditing.IDPromptShape, tmpRecord);
		}
		else
		{
			tmpSave = this._api().create(Object.assign(
			{
				Scope: tmpState.Scope,
				ShapeKey: tmpEditing.ShapeKey
			}, tmpRecord));
		}
		let tmpSelf = this;
		tmpSave.then(() =>
		{
			tmpState.Editing = null;
			tmpSelf._toast('Shape saved.', 'success');
			// On the standalone routed page, saving returns to /prompt-shapes (leaving /prompt-shape/:id).
			let tmpApp = tmpSelf.pict.PictApplication;
			if (tmpSelf._routed && tmpApp && typeof tmpApp.showPromptShapes === 'function') { tmpApp.showPromptShapes(); return; }
			return tmpSelf._reload();
		}).catch((pError) => tmpSelf._toast(pError.message, 'error'));
	}

	_toast(pMessage, pType)
	{
		let tmpModal = this._modal();
		if (tmpModal && typeof tmpModal.toast === 'function') { tmpModal.toast(pMessage, { type: pType || 'info' }); }
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		this._paintLists();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = PictViewPromptShapes;
module.exports.default_configuration = _ViewConfiguration;
